/**
 * Print pipeline.
 *
 * `printPdfViaIframe(payload)`:
 *   1. Create a hidden iframe.
 *   2. Write a minimal HTML doc into it.
 *   3. Inject our print stylesheet + the design tokens stylesheet.
 *   4. Render the report React tree into the iframe's body via createRoot
 *      + flushSync.
 *   5. Wait for fonts to load (best-effort).
 *   6. Call `iframe.contentWindow.print()`.
 *   7. Tear down the iframe after the print dialog closes.
 *
 * Ported from `jackhammer-ui/src/lib/reportRenderer/rendering.jsx`.
 */

import { flushSync } from 'react-dom';
import { createRoot } from 'react-dom/client';
import type { Card, CheckSummary } from '@eloquor/api-contract';
import { ReportMain } from './components/ReportMain';
import { ensureReportFonts, injectPrintStyle, injectStylesheet, writeReportDocument } from './dom';

export interface ReportPayload {
  cards: Card[];
  summary: CheckSummary;
  title?: string;
}

export async function printPdfViaIframe(payload: ReportPayload): Promise<void> {
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.style.opacity = '0';
  iframe.setAttribute('aria-hidden', 'true');
  document.body.appendChild(iframe);

  const doc = iframe.contentDocument;
  if (!doc) return;
  writeReportDocument(doc, payload.title ?? 'Eloquor report');
  injectStylesheet(doc, '/design/colors_and_type.css');
  injectPrintStyle(doc);

  const container = doc.createElement('div');
  container.className = 'report-page';
  doc.body.appendChild(container);

  const root = createRoot(container);
  flushSync(() => {
    root.render(<ReportMain cards={payload.cards} summary={payload.summary} />);
  });

  await ensureReportFonts(doc);

  // The print dialog blocks until dismissed; cleanup happens after.
  iframe.contentWindow?.focus();
  iframe.contentWindow?.print();

  // Defer teardown so the browser doesn't yank the iframe before the
  // print dialog has fully serialized.
  setTimeout(() => {
    root.unmount();
    iframe.remove();
  }, 1000);
}
